import { useLocalStorage } from '@mantine/hooks';
import type { HyperlineEntities } from '@vrplatform/hyperline-client';
import type { currency_enum } from '@vrplatform/graphql';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { isUUID, utc } from '@vrplatform/utils';
import { VRP_TENANT_ID } from '@vrplatform/ui-common';
import { useEffect, useRef } from 'react';
import { useApiQuery } from '../../api';
import { useQuery, useQueryClient } from '../../graphql';
import { useExtension } from '../useExtension';
import { useTeamIdContext } from './team.context';
import type { Team } from './team.types';
import { isStorybook } from '../../env';

type ApiTeamHistoricMigrationFields = {
  migratedFromTenantId?: string | null;
  historicalStatementsImportedAt?: string | null;
};

export function _useLocalStorageTeamId(userId: string | undefined) {
  return useLocalStorage<string | undefined | null>({
    key: `${userId}_tid`,
    defaultValue: undefined,
    getInitialValueInEffect: false,
  });
}

export function useUserQuery(args: {
  userId: string | undefined;
  role: string | undefined;
}) {
  return useQuery(
    (q, { userId, VRP_TENANT_ID, isOwner }) => {
      if (!userId)
        return {
          user: null,
        };

      const user =
        q
          .user({
            where: {
              id: { _eq: userId },
            },
            limit: 1,
          })
          .map((user) => {
            const partnerMemberships = user
              ?.memberships({
                where: {
                  _or: [
                    {
                      tenant: {
                        // Partner admin
                        type: { _eq: 'partner' },
                      },
                      role: { _eq: 'admin' },
                      status: { _eq: 'active' },
                    },
                    {
                      // VRP admin
                      tenantId: { _eq: VRP_TENANT_ID },
                      status: { _eq: 'active' },
                    },
                  ],
                },
              })
              .map((membership) => ({
                status: membership?.status,
                id: membership?.tenantId,
                role: membership?.role,
                teamType: membership?.tenant?.type,
              }));

            const isSuperAdmin = user.isAdmin;

            const hasPartnerMembership = partnerMemberships.some(
              (x) => x.id === VRP_TENANT_ID
            );

            const hasPartnerAdminMembership =
              partnerMemberships.find((x) => x.teamType === 'partner')?.role ===
              'admin';

            const isVrpAdmin = isSuperAdmin || hasPartnerMembership;

            const isPartnerAdmin =
              isSuperAdmin || isVrpAdmin || hasPartnerAdminMembership;

            return {
              id: user.id!,
              type: user.type!,
              name: user.name!,
              firstName: user.firstName,
              lastName: user.lastName,
              partnerId: user.partnerId,
              ownerAccess: isOwner
                ? user.ownerAccesses().map((x) => {
                    const contactTenantId = x.contact?.tenantId;
                    const ownerTenantId = x.owner?.tenantId;

                    return {
                      contactId: x.contactId ?? undefined,
                      ownerId: x.ownerId ?? undefined,
                      tenantId: (contactTenantId || ownerTenantId)!,
                      role: x.role || 'viewer',
                    };
                  })
                : [],
              isSuperAdmin,
              isPartnerAdmin,
              isVrpAdmin,
              email: user.email!,
              createdAt: user.createdAt!,
              featureApprovals: user.featureApprovals().map((approval) => ({
                id: approval.id,
                status: approval.status ?? undefined,
                featureId: approval.featureId,
              })),
            };
          })[0] || null;

      return {
        user,
      };
    },
    {
      skip: !args.userId || isStorybook,
      keepPreviousData: true,
      variables: {
        userId: args.userId,
        VRP_TENANT_ID,
        isOwner: args.role === 'owner',
      },
      queryKey: ['users', 'featureApprovals'],
      teamScoped: false,
    }
  );
}

export function useTeamApiQuery(args: { userId: string | undefined }) {
  const { teamId, setTeamId } = useTeamIdContext();
  const isTeamIdBootstrapping = teamId === undefined && !!args.userId;
  const [sti, setSti] = useQueryState('sti', parseAsString);
  const { sendMessage } = useExtension();
  const queryClient = useQueryClient();
  const lastTeamQueryKeyRef = useRef<readonly unknown[] | null>(null);

  const { isLoading: loadingSti, data: stiData } = useQuery(
    (q, args) => {
      if (!args.sti || !isUUID(args.sti)) return null;

      return q
        .tenant({
          where: {
            id: {
              _eq: args.sti,
            },
          },
          limit: 1,
        })
        .map((t) => ({
          id: t.id,
        }))[0];
    },
    {
      skip: !sti,
      variables: {
        sti,
      },
    }
  );

  const queryData = useApiQuery(
    async (api, signal, args, hasura) => {
      if (
        !args.userId ||
        !isUUID(args.userId) ||
        (args.activeTeamId && !isUUID(args.activeTeamId))
      )
        return {
          team: null,
        };

      if (!args.activeTeamId) {
        return {
          team: null,
        };
      }

      if (!isUUID(args.activeTeamId)) {
        return {
          team: null,
        };
      }

      const teamId = args.activeTeamId;

      const [apiTeam, dbTeam] = await Promise.all([
        api
          .GET('/teams/{id}', {
            params: {
              path: {
                id: teamId,
              },
            },
            signal,
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data;
          }),
        hasura(
          (q) => {
            const team =
              q
                .tenant({
                  limit: 1,
                  where: { id: { _eq: teamId } },
                })
                .map((item) => {
                  const membership = item
                    .members({ where: { userId: { _eq: args.userId } } })
                    .map((x) => ({
                      id: x.id,
                      role: x.role,
                      status: x.status,
                    }))[0];

                  return {
                    id: item.id,
                    membership,
                    billingPaymentMethodType: item.billingPaymentMethodType,
                    billingCustomerId: item.billingCustomerId,
                    colorPrimary: item.colorPrimary,
                    enabledFeatures: item.enabledFeatures()?.map((x) => ({
                      featureId: x.featureId,
                      status: x.status,
                      updatedAt: x.updatedAt,
                    })),
                    forceTwoFactorAuth: !!item
                      .settings({
                        where: {
                          key: { _eq: 'tenantSettings' },
                          target: { _eq: 'forceTwoFactorAuth' },
                        },
                        limit: 1,
                      })
                      ?.map((x) => x.id)[0],
                  };
                })[0] ?? null;

            return team;
          },
          {
            fetchOptions: {
              signal,
            },
          }
        ),
      ]);

      if (!apiTeam || !dbTeam) {
        return {
          team: null,
        };
      }

      const membership = dbTeam.membership;
      const apiMemberRole = apiTeam.members?.find(
        (member) => member.userId === args.userId
      )?.role;
      const apiTeamWithHistoricMigrationFields = apiTeam as typeof apiTeam &
        ApiTeamHistoricMigrationFields;

      return {
        team: {
          role: (membership?.role || apiMemberRole) as
            | 'admin'
            | 'owner'
            | 'user',
          membershipId: membership?.id,
          membershipStatus: membership?.status,
          id: apiTeam.id,
          statementStartAt:
            apiTeam.statementStartAt && utc(apiTeam.statementStartAt).isValid()
              ? utc(apiTeam.statementStartAt).yyyymmdd()
              : null,
          effectiveStatementStartAt:
            apiTeam.effectiveStatementStartAt &&
            utc(apiTeam.effectiveStatementStartAt).isValid()
              ? utc(apiTeam.effectiveStatementStartAt).yyyymmdd()
              : null,
          companyName: apiTeam.companyName,
          email: apiTeam.email,
          phone: apiTeam.phone,
          taxId: apiTeam.taxId,
          billingAddress: apiTeam.billingAddress,
          logo: apiTeam.logo!,
          name: apiTeam.name,
          migratedFromTenantId:
            apiTeamWithHistoricMigrationFields.migratedFromTenantId ?? null,
          historicalStatementsImportedAt:
            apiTeamWithHistoricMigrationFields.historicalStatementsImportedAt ??
            null,
          extractableConnections: apiTeam.extractableConnections,
          issues: apiTeam.issues,
          defaultRevenueRecognition:
            apiTeam.defaultRevenueRecognition ?? 'checkIn',
          ownerPortalSettings: {
            showDraftStatementsOnSummaries:
              apiTeam.ownerPortalShowDraftStatements ?? false,
          },
          isGL: apiTeam.isGeneralLedger ?? false,
          currency: apiTeam.defaultCurrency ?? ('usd' satisfies currency_enum),
          subscription: {
            cancelledAt: (apiTeam.cancelledAt
              ? utc(apiTeam.cancelledAt).yyyymmdd()
              : null) as string | null,
            trialUntil: (apiTeam.trialUntil
              ? utc(apiTeam.trialUntil).yyyymmdd()
              : null) as string | null,
            status:
              apiTeam.billingSubscriptionStatus as HyperlineEntities['Subscription']['status'],
            paymentMethodType: (dbTeam.billingPaymentMethodType || null) as
              | NonNullable<HyperlineEntities['PaymentMethod']>['type']
              | null,
            customerId: dbTeam.billingCustomerId,
            plan: apiTeam.billingPlan,
          },
          status: apiTeam.status as 'active' | 'inactive',
          isOnboarding: !!apiTeam.isOnboarding,
          createdAt: apiTeam.createdAt!,
          colorPrimary: dbTeam.colorPrimary!,
          booksClosedAt: apiTeam.booksClosedAt
            ? utc(apiTeam.booksClosedAt).subtract(1, 'day').yyyymmdd()
            : null,
          partnerId: apiTeam.partner?.id || VRP_TENANT_ID,
          partnerName: apiTeam.partner?.name,
          billingPartnerId: apiTeam.billingPartner?.id || apiTeam.partner?.id,
          enabledFeatures: dbTeam.enabledFeatures,
          forceTwoFactorAuth: dbTeam.forceTwoFactorAuth,
          type: apiTeam.type === 'admin' ? 'partner' : apiTeam.type,
        },
      };
    },
    {
      skip: !args.userId || isTeamIdBootstrapping || isStorybook,
      allowDuringTeamSwitch: true,
      variables: {
        userId: args.userId,
        activeTeamId: teamId,
      },
      keepPreviousData: true,
      queryGroup: 'teams',
      uniqueQueryKey: 'team',
      queryKeys: ['teams', 'featureApprovals'],
    }
  );

  useEffect(() => {
    if (queryData.data?.team) {
      lastTeamQueryKeyRef.current = queryData.queryKey;
    }
  }, [queryData.data?.team, queryData.queryKey]);

  const cachedData =
    !queryData.data && queryData.isFetching && lastTeamQueryKeyRef.current
      ? queryClient.getQueryData<{
          team: Team | null;
        }>(lastTeamQueryKeyRef.current)
      : undefined;

  const team = (queryData.data ?? cachedData)?.team;

  const isStaleSelectedTeam =
    !isTeamIdBootstrapping &&
    !!teamId &&
    !queryData.isFetching &&
    (queryData.data?.team === null ||
      queryData.error?.code === 'NOT_FOUND' ||
      queryData.error?.code === 'FORBIDDEN');

  useEffect(() => {
    if (!isStaleSelectedTeam) return;

    setTeamId(null);
  }, [isStaleSelectedTeam, setTeamId]);

  useEffect(() => {
    if (!stiData?.id) return;

    if (stiData.id === team?.id) {
      setSti(null);
      if (teamId !== stiData.id) {
        setTeamId(stiData.id);
      }
      sendMessage({ message: 'team_changed', data: { teamId: stiData.id } });
      return;
    }

    if (teamId !== stiData.id) {
      setTeamId(stiData.id);
    }
  }, [stiData?.id, team?.id, teamId, setSti, setTeamId, sendMessage]);

  const isLoading = loadingSti || queryData.isLoading || isTeamIdBootstrapping;
  const isFetching = queryData.isFetching || isTeamIdBootstrapping;

  return {
    ...queryData,
    data: queryData.data ?? cachedData,
    isLoading,
    isFetching,
  };
}
